import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    login as authLogin,
    register as authRegister,
    logout as authLogout,
    checkAutoLogin,
    requestPasswordReset as authRequestPasswordReset,
    resetPassword as authResetPassword
} from '../services/authService';
import { localStorageService } from '../services/localStorageService';

/**
 * Identity, and nothing else.
 *
 * Under D22 there are three states, not two:
 *
 *   no token          - a visitor who hasn't written anything. `user` is null.
 *   guest token       - a real `users` record with no credentials attached.
 *                       `user` is set, `isAuthenticated` is false.
 *   credentialed      - the same record, with a username and password or a
 *                       Google id attached. `isAuthenticated` is true.
 *
 * `isAuthenticated` deliberately means "has credentials", not "has a record",
 * because that's the distinction the UI cares about: whether this person can
 * get back in from another device. Anything gated on merely *having* an
 * identity should look at `user` instead.
 *
 * Preferences live in PrefsContext and plays in PlayContext; both watch
 * `user?.id` and reload when it changes.
 */
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // One-time hygiene: drop state written by the pre-D22 client.
        localStorageService.clearRetiredState();

        checkAutoLogin()
            .then((restoredUser) => {
                setUser(restoredUser);
            })
            .catch((error) => {
                console.log('Error during auto-login:', error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const login = async (email, password) => {
        setIsLoading(true);
        try {
            const authenticatedUser = await authLogin(email, password);
            setUser(authenticatedUser);
            return authenticatedUser;
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (username, password, email) => {
        setIsLoading(true);
        try {
            const authenticatedUser = await authRegister(username, password, email);
            setUser(authenticatedUser);
            return authenticatedUser;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        await authLogout();
        setUser(null);
    };

    const requestPasswordReset = async (email) => {
        await authRequestPasswordReset(email);
    };

    // Signs the user in as a side effect - spending the token proves control
    // of the account's email address.
    const resetPassword = async (token, password) => {
        const recoveredUser = await authResetPassword(token, password);
        setUser(recoveredUser);
        return recoveredUser;
    };

    /**
     * Re-read the identity from the server. Needed after a write creates the
     * user record on the fly, so the rest of the app learns there's now an
     * identity to hang state off.
     */
    const refreshUser = useCallback(async () => {
        const restoredUser = await checkAutoLogin();
        setUser(restoredUser);
        return restoredUser;
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            isLoading,
            isAuthenticated: !!user && !user.is_guest,
            isGuest: !!user && !!user.is_guest,
            login,
            register,
            logout,
            refreshUser,
            requestPasswordReset,
            resetPassword
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
